import { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { FiTrash2, FiRefreshCw, FiXCircle, FiCheckCircle } from 'react-icons/fi';
import { useReports } from '../context/ReportContext';
import { useAuth } from '../context/AuthContext';
import Modal from './Modal';
import AppSwitcher from './AppSwitcher';
import { normalizeStatus, isFailedStatus, isCancelledStatus, isInProgressStatus, isCompleteStatus, formatReportDate } from '../utils/reportHelpers';
import logo from '../assets/logo.png';
import pcTabClicked from '../assets/pc-tab-clicked.png';
import pcTabUnclicked from '../assets/pc-tab-unclicked.png';
import libraryTabClicked from '../assets/library-tab-clicked.png';
import libraryTabUnclicked from '../assets/library-tab-unclicked.png';
import { PRODUCT_VERSION } from '../config/version';
import './Sidebar.css';

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const activeRunId = searchParams.get('runId');
  const { user, platformOrgModules } = useAuth();
  const { reports, toggleBookmark, isBookmarked, deleteReport, cancelReport } = useReports();
  const [activeTab, setActiveTab] = useState(() => {
    return sessionStorage.getItem('sidebarActiveTab') || 'history';
  });
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, reportId: null, error: null, isDeleting: false });
  const [cancellingId, setCancellingId] = useState(null);

  const isPolicyCheckerPage = location.pathname === '/policy-checker';

  useEffect(() => {
    sessionStorage.setItem('sidebarActiveTab', activeTab);
  }, [activeTab]);

  const displayReports = activeTab === 'history'
    ? reports
    : reports.filter((r) => isBookmarked(r.id));

  const handleDeleteClick = (e, reportId) => {
    e.stopPropagation();
    setDeleteModal({ isOpen: true, reportId, error: null, isDeleting: false });
  };

  const handleDeleteConfirm = async () => {
    setDeleteModal(prev => ({ ...prev, isDeleting: true, error: null }));
    try {
      await deleteReport(deleteModal.reportId);
      setDeleteModal({ isOpen: false, reportId: null, error: null, isDeleting: false });
    } catch (error) {
      console.error('Failed to delete report:', error);
      // Show user-friendly error message instead of just closing the modal
      const errorMessage = error?.userMessage || error?.message || 'Failed to delete report. Please try again.';
      setDeleteModal(prev => ({ ...prev, error: errorMessage, isDeleting: false }));
    }
  };

  const handleCancelClick = async (e, reportId) => {
    e.stopPropagation();
    setCancellingId(reportId);
    try {
      await cancelReport(reportId);
    } catch (error) {
      console.error('Failed to cancel report:', error);
    } finally {
      setCancellingId(null);
    }
  };

  const handleReportClick = (report) => {
    const normalizedStatus = normalizeStatus(report.status);

    if (isFailedStatus(normalizedStatus)) {
      return;
    }

    if (isInProgressStatus(normalizedStatus)) {
      return;
    }

    // For completed reports, navigate to policy-checker with runId
    navigate(`/policy-checker?runId=${report.id}`);
  };

  return (
    <aside className="sidebar">
      <div className="sidebar-logo">
        <AppSwitcher enabledModules={user?.enabled_modules || ''} orgModules={platformOrgModules} />
        <img src={logo} alt="Crime Central Logo" className="logo-image" />
        <h2>CRIME CENTRAL</h2>
      </div>

      <nav className="sidebar-nav">
        <NavLink to="/policy-checker" className="nav-item-image">
          {({ isActive }) => (
            <img
              src={isActive ? pcTabClicked : pcTabUnclicked}
              alt="Policy checker"
              className="nav-item-img"
            />
          )}
        </NavLink>
      </nav>

      {isPolicyCheckerPage && (
        <>
          <div className="history-tabs">
            <button
              className={`tab-button ${activeTab === 'history' ? 'active' : ''}`}
              onClick={() => setActiveTab('history')}
            >
              History
            </button>
            <button
              className={`tab-button ${activeTab === 'bookmarks' ? 'active' : ''}`}
              onClick={() => setActiveTab('bookmarks')}
            >
              Bookmarks
            </button>
          </div>

          <div className="history-list">
            {displayReports.length === 0 ? (
              <div className="empty-history">
                <p>{activeTab === 'history' ? 'No reports yet' : 'No bookmarks yet'}</p>
              </div>
            ) : (
              displayReports.map((report) => {
                const isProcessing = isInProgressStatus(report.status);
                const isFailed = isFailedStatus(report.status);
                const isCompleted = isCompleteStatus(report.status);

                return (
                  <div
                    key={report.id}
                    className={`history-item ${isFailed ? 'failed' : ''} ${isProcessing ? 'processing' : ''} ${activeRunId === report.id ? 'active' : ''}`}
                    onClick={() => handleReportClick(report)}
                    style={{ cursor: isProcessing ? 'not-allowed' : 'pointer' }}
                  >
                    <div className="history-item-header">
                      <div className="report-info">
                        <span className="report-date">
                          {formatReportDate(report.createdAt, {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                          {report.createdBy && ` · ${
                            report.createdBy.includes('@')
                              ? report.createdBy.split('@')[0]
                              : report.createdBy
                          }`}
                        </span>
                        {isCancelledStatus(report.status) && (
                          <FiXCircle className="status-icon status-cancelled" />
                        )}
                        {isFailed && (
                          <FiXCircle className="status-icon status-failed" />
                        )}
                      </div>
                      {isProcessing && (
                        <span className="in-progress-badge">
                          In progress <FiRefreshCw className="in-progress-spin" />
                        </span>
                      )}
                      {isCompleted && (
                        <span className="complete-badge">
                          Complete <FiCheckCircle size={12} />
                        </span>
                      )}
                      <div className="history-item-actions">
                        {isCompleted && (
                          <button
                            className="view-btn"
                            onClick={(e) => { e.stopPropagation(); navigate(`/policy-checker?runId=${report.id}`); }}
                          >
                            View
                          </button>
                        )}
                        {!isProcessing && (
                          <button
                            className={`bookmark-btn ${isBookmarked(report.id) ? 'bookmarked' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleBookmark(report.id);
                            }}
                            aria-label={isBookmarked(report.id) ? 'Remove bookmark' : 'Add bookmark'}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M19 21L12 16L5 21V5C5 4.46957 5.21071 3.96086 5.58579 3.58579C5.96086 3.21071 6.46957 3 7 3H17C17.5304 3 18.0391 3.21071 18.4142 3.58579C18.7893 3.96086 19 4.46957 19 5V21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                        )}
                        <button
                          className="delete-btn"
                          onClick={(e) => isProcessing ? handleCancelClick(e, report.id) : handleDeleteClick(e, report.id)}
                          aria-label={isProcessing ? 'Cancel run' : 'Delete report'}
                          disabled={cancellingId === report.id}
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="sidebar-library-divider">
            <NavLink to="/library" className="nav-item-image">
              {({ isActive }) => (
                <img
                  src={isActive ? libraryTabClicked : libraryTabUnclicked}
                  alt="Library"
                  className="nav-item-img"
                />
              )}
            </NavLink>
          </div>
        </>
      )}

      {!isPolicyCheckerPage && (
        <NavLink to="/library" className="nav-item-image">
          {({ isActive }) => (
            <img
              src={isActive ? libraryTabClicked : libraryTabUnclicked}
              alt="Library"
              className="nav-item-img"
            />
          )}
        </NavLink>
      )}

      <Modal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, reportId: null, error: null, isDeleting: false })}
        onConfirm={handleDeleteConfirm}
        title="Delete Report"
        message="Are you sure you want to delete this report? This action cannot be undone."
        confirmText={deleteModal.isDeleting ? 'Deleting...' : 'Delete'}
        cancelText="Cancel"
        danger={true}
        error={deleteModal.error}
        isLoading={deleteModal.isDeleting}
      />

      <div data-testid="sidebar-version" style={{ marginTop: 'auto', padding: '8px', fontSize: '11px', opacity: 0.5 }}>
        {PRODUCT_VERSION}
      </div>
    </aside>
  );
}
